import { useRef, useCallback } from "react";

const BUFFER_SIZE = 4800; // 100ms at 24kHz 16-bit mono

/**
 * Hook for recording microphone audio as PCM 16-bit 24kHz mono.
 * Returns raw PCM bytes via the onAudioData callback.
 */
export function useAudioRecorder(onAudioData: (data: ArrayBuffer) => void) {
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);

    let buffer = new Uint8Array();

    const start = useCallback(async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;

        const audioContext = new AudioContext({ sampleRate: 24000 });
        audioContextRef.current = audioContext;

        await audioContext.audioWorklet.addModule("/audio-processor-worklet.js");

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, "audio-processor-worklet");
        workletNodeRef.current = workletNode;

        workletNode.port.onmessage = (event: MessageEvent) => {
            const int16Data = new Int16Array(event.data.buffer || event.data);
            const bytes = new Uint8Array(int16Data.buffer);

            const newBuffer = new Uint8Array(buffer.length + bytes.length);
            newBuffer.set(buffer);
            newBuffer.set(bytes, buffer.length);
            buffer = newBuffer;

            while (buffer.length >= BUFFER_SIZE) {
                const chunk = buffer.slice(0, BUFFER_SIZE);
                buffer = buffer.slice(BUFFER_SIZE);
                onAudioData(chunk.buffer);
            }
        };

        source.connect(workletNode);
        workletNode.connect(audioContext.destination);
    }, [onAudioData]);

    const stop = useCallback(async () => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
            await audioContextRef.current.close();
            audioContextRef.current = null;
        }
        workletNodeRef.current = null;
        buffer = new Uint8Array();
    }, []);

    return { start, stop };
}
